/**
 * Kuvr Modal System
 * Loads content from Shopify pages via Ajax and displays in a modal
 */

/* BUG-021 fix 1 of 3 — lock the page behind the modal.
 * Evidence: Duncan, on device — "I can see the background scrolling behind the
 * modal", and after this change, "I can't see it moving". The only change all
 * session that fixed a symptom he could see.
 * `body{overflow:hidden}` alone does not stop touch scrolling on iOS. Chrome
 * honours it, which is why this read as locked in every check made off-device.
 * Guarded on there being no scrollbar to reclaim: on a phone that is always
 * true, on desktop never, so the 5px layout shift this caused in DevTools
 * (an emulated scrollbar) is impossible by construction.
 */
const KuvrScrollLock = {
  locked: false,
  prev: null,
  lock() {
    if (this.locked) return;
    this.locked = true;
    const de = document.documentElement;
    const scrollbar = window.innerWidth - de.clientWidth;
    this.prev = {
      htmlOverflow: de.style.overflow,
      htmlOverscroll: de.style.overscrollBehavior,
      bodyOverflow: document.body.style.overflow,
      lockedHtml: scrollbar === 0
    };
    if (scrollbar === 0) {
      de.style.overflow = 'hidden';
      de.style.overscrollBehavior = 'none';   // also blocks pull-to-refresh while open
    }
    document.body.style.overflow = 'hidden';
  },
  unlock() {
    if (!this.locked) return;
    this.locked = false;
    const de = document.documentElement;
    const p = this.prev || {};
    if (p.lockedHtml) {
      de.style.overflow = p.htmlOverflow || '';
      de.style.overscrollBehavior = p.htmlOverscroll || '';
    }
    document.body.style.overflow = p.bodyOverflow || '';
    this.prev = null;
  }
};

class KuvrModal {
  constructor() {
    this.modal = null;
    this.modalContent = null;
    this.modalClose = null;
    this.init();
  }

  init() {
    // Create modal HTML structure
    this.createModal();

    // Bind event listeners
    this.bindEvents();
    this.watchForForeignOpens();
  }

  createModal() {
    // Create modal container
    const modalHTML = `
      <div class="kuvr-modal" id="kuvrModal" aria-hidden="true" role="dialog" aria-modal="true">
        <div class="kuvr-modal__overlay" data-modal-close></div>
        <div class="kuvr-modal__container">
          <button class="kuvr-modal__close" data-modal-close aria-label="Close modal">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="kuvr-modal__content" id="kuvrModalContent">
            <!-- Content loaded here -->
          </div>
        </div>
      </div>
    `;

    // Append to body
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Cache references
    this.modal = document.getElementById('kuvrModal');
    this.modalContent = document.getElementById('kuvrModalContent');
    this.modalClose = this.modal.querySelectorAll('[data-modal-close]');
  }

  /* BUG-025 (2026-08-15) — takeover must not depend on WHO opened the modal.
   *
   * open() applies takeover on mobile, but not every opener goes through open().
   * The DPO measuring guide is MOVED into #kuvrModalContent by the Itoris global
   * JS, which then shows the modal by hand:
   *     modal.classList.add('is-active');
   *     modal.setAttribute('aria-hidden', 'false');
   *     document.body.style.overflow = 'hidden';
   * That skips _enterTakeover() entirely, so the guide fell back to the inner
   * #kuvrModalContent scroller — the exact mechanism BUG-021 proved unusable on
   * this page (jQuery Mobile's vmouse layer, the drawer component's
   * preventDefault() on any near-horizontal touchmove, ~48 non-passive element
   * listeners). Symptom on device: a swipe started on the guide's text did
   * nothing and the PRODUCT PAGE scrolled behind the modal instead; a swipe
   * started on a thumbnail or the diagram worked, because those carry their own
   * listeners. body{overflow:hidden} does not lock the document on iOS.
   *
   * Fixing it in the Itoris admin would fix ONE opener. Watching the modal's own
   * state fixes every opener, including any added later, and keeps the fix in the
   * theme where it is version-controlled. The observer is the authority on
   * takeover; open() still calls _enterTakeover() directly so the common path
   * does not wait a microtask.
   */
  watchForForeignOpens() {
    const isMobile = () => window.matchMedia('(max-width: 768px)').matches;
    const inTakeover = () =>
      document.documentElement.classList.contains('kuvr-takeover');

    new MutationObserver(() => {
      const open = this.modal.classList.contains('is-active');
      if (open && isMobile() && !inTakeover()) {
        // Opened by something other than open(). Same treatment.
        this._enterTakeover();
      } else if (!open && inTakeover()) {
        // Closed by something that did not route through close().
        this._exitTakeover();
      }
    }).observe(this.modal, {
      attributes: true,
      attributeFilter: ['class', 'aria-hidden'],
    });
  }

  bindEvents() {
    // Listen for clicks on elements with data-modal-url
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-modal-url]');
      if (trigger) {
        e.preventDefault();
        const url = trigger.getAttribute('data-modal-url');
        const fabrics = trigger.getAttribute('data-modal-fabrics');
        this.open(url, fabrics);
      }
    });

    // Close modal events
    this.modalClose.forEach(el => {
      el.addEventListener('click', () => this.close());
    });

    // Takeover mode: the document is the modal's scroller, so a bare
    // href="#" link inside modal content (e.g. "Full guarantee terms apply",
    // which opens a Help Scout Beacon article from JS) would jump the document
    // — i.e. the modal — back to the top before its JS handler runs. Kill the
    // default navigation only; the JS handlers still fire.
    document.addEventListener('click', (e) => {
      if (!document.documentElement.classList.contains('kuvr-takeover')) return;
      const a = e.target.closest('#kuvrModalContent a[href="#"], #kuvrModalContent a[href=""]');
      if (a) e.preventDefault();
    });

    // ESC key: close video lightbox first, then modal on second press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const lb = document.getElementById('fabricVideoLightbox');
        const fr = document.getElementById('fabricVideoFrame');
        if (lb && lb.classList.contains('is-active')) {
          lb.classList.remove('is-active');
          if (fr) fr.src = '';
          // Do NOT unlock here — the lightbox sits inside a still-open modal.
        } else if (this.isOpen()) {
          // Route Escape through the close control (a [data-modal-close] element),
          // NOT close() directly. Some content is MOVED into the modal rather than
          // fetched — the DPO "Open Measuring Guide" moves the live #kuvr-measuring-guide
          // element into #kuvrModalContent. The restore that moves it back home is bound
          // to the close controls (overlay/button click). close() alone skips those
          // listeners, and its 300ms innerHTML wipe then destroys the borrowed element,
          // so the guide cannot reopen until a hard refresh (BUG-014). Clicking a close
          // control fires the restore first, then close() — matching the X/overlay path.
          const closeCtrl = this.modal.querySelector(".kuvr-modal__close") || this.modal.querySelector("[data-modal-close]");
          if (closeCtrl) { closeCtrl.click(); } else { this.close(); }
        }
      }
    });
  }

  /* Anchored modal links — e.g. the DPO fasteners guide link opens
   * /pages/tie-downs-fasteners-shaped-covers#fasteners. The old mechanism (a
   * MutationObserver in theme.liquid that sets container.scrollTop) silently
   * broke in takeover mode: the content box is no longer a scroller there
   * (overflow:visible), so writing its scrollTop is a no-op — the DOCUMENT is
   * the scroller. This handles any #fragment on any data-modal-url, in both
   * modes. Runs twice (early + after images settle) because late image loads
   * shift the target's position; both passes are instant and idempotent. */
  scrollToAnchor(id) {
    const inTakeover = () => document.documentElement.classList.contains('kuvr-takeover');
    const findTarget = () => document.getElementById(id) ||
      this.modalContent.querySelector('[name="' + CSS.escape(id) + '"]');
    let lastSet = null;
    const cur = () => inTakeover() ? window.scrollY : this.modalContent.scrollTop;
    const place = (t) => {
      if (inTakeover()) {
        window.scrollTo({ top: Math.max(0, t.getBoundingClientRect().top + window.scrollY - 70), behavior: 'instant' });
        lastSet = window.scrollY;
      } else {
        const c = this.modalContent;
        c.scrollTop += t.getBoundingClientRect().top - c.getBoundingClientRect().top - 20;
        lastSet = c.scrollTop;
      }
    };
    /* The content keeps growing for several seconds after injection — images
       above the anchor render at zero height and then expand as they decode,
       which pushed a one-shot scroll ~390px adrift. A timed loop dies too
       early on slow connections, so track reflow itself: every resize of the
       content re-places the anchor, until the user scrolls (position deviates
       from the last value we set) or 10s passes. */
    if (this._anchorRO) this._anchorRO.disconnect();
    const settle = () => {
      /* No position-deviation heuristic here — it misfired: when late layout
         changes make the document temporarily SHORTER, the browser clamps
         scrollY (measured: 1485 -> 659), which looks exactly like a user
         scroll. Real user cancellation comes from the trusted-input listeners
         below; nothing else may stop the settle. */
      const t = findTarget();
      if (!t) return;
      const off = inTakeover()
        ? t.getBoundingClientRect().top - 70
        : t.getBoundingClientRect().top - this.modalContent.getBoundingClientRect().top - 20;
      if (lastSet === null || Math.abs(off) > 60) place(t);
    };
    const ro = new ResizeObserver(settle);
    this._anchorRO = ro;
    ro.observe(this.modalContent);
    const poll = setInterval(settle, 250);       // catches target arriving late
    const EVENTS = ['touchmove', 'wheel', 'keydown'];
    const onInput = (e) => {
      // Page scripts dispatch synthetic touch events at load (isTrusted:false)
      // — only real user input may cancel the anchor. And touchMOVE, not
      // touchstart: a tap is not scroll intent, movement is.
      if (!e.isTrusted) return;
      if (e.type === 'keydown' &&
          !/^(Arrow(Up|Down)|Page(Up|Down)|Home|End| )$/.test(e.key)) return;
      stop();
    };
    const stop = () => {
      clearInterval(poll); ro.disconnect();
      EVENTS.forEach(ev => document.removeEventListener(ev, onInput));
    };
    EVENTS.forEach(ev => document.addEventListener(ev, onInput, { passive: true }));
    setTimeout(stop, 10000);
    settle();
  }

  async open(url, fabrics) {
    this._anchor = (url && url.indexOf('#') !== -1) ? url.split('#')[1] : null;
    // Show loading state
    this.showLoading();

    // BUG-021 FINAL APPROACH — mobile: no inner scroller at all.
    // Desktop keeps the classic overlay + KuvrScrollLock.
    if (window.matchMedia('(max-width: 768px)').matches) {
      this._enterTakeover();
    } else {
      KuvrScrollLock.lock();
    }

    // Show modal
    this.modal.setAttribute('aria-hidden', 'false');
    this.modal.classList.add('is-active');

    try {
      /* GOTCHA #4, fixed 2026-08-16. The storefront edge-caches this AJAX fetch, so a
         freshly-deployed page change could show STALE in the modal for several minutes
         while the standalone page was already correct. Duncan hit it head-on: "it doesn't
         seem like any of your updates have applied to the modal version" — the modal was
         serving a copy of /pages/shipping from a few deploys earlier, showing the old
         action links AND the new ones at the same time.

         Two parts, because one is not enough:
           cache:'reload'  bypasses the BROWSER's http cache;
           _cb bucket      defeats the EDGE cache, which ignores request cache modes.
         The bucket changes once a minute rather than on every open, so the CDN can still
         serve repeat opens within the same minute — freshness where it matters, without
         forcing an origin hit for every single modal open on every product page. */
      const bucket = Math.floor(Date.now() / 60000);
      const bust = url + (url.indexOf('?') === -1 ? '?' : '&') + '_cb=' + bucket;
      const response = await fetch(bust, { cache: 'reload' });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const html = await response.text();

      // Parse HTML and extract .modal-article-content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const content = doc.querySelector('.modal-article-content');

      if (content) {
        // Inject content into modal
        this.modalContent.innerHTML = content.innerHTML;

        // innerHTML strips script execution — re-run each inline script
        this.modalContent.querySelectorAll("script").forEach(oldScript => {
          const newScript = document.createElement("script");
          newScript.textContent = oldScript.textContent;
          document.head.appendChild(newScript);
          document.head.removeChild(newScript);
        });

        // Per-product fabric column filtering (fabrics comparison modal only)
        this.filterFabrics(fabrics);

        if (this._anchor) this.scrollToAnchor(this._anchor);

        // BUG-021 — see makeDecorationsTouchTransparent()
        this.makeDecorationsTouchTransparent();
        setTimeout(() => this.makeDecorationsTouchTransparent(), 800);
        setTimeout(() => this.makeDecorationsTouchTransparent(), 2000);
      } else {
        // Fallback if wrapper not found
        throw new Error('Modal content wrapper not found');
      }

    } catch (error) {
      console.error('Error loading modal content:', error);
      this.showError(url);
    }
  }

  // Hide fabric columns this product does not sell. `fabrics` = CSV of allowed
  // data-fabric keys (from the trigger's data-modal-fabrics). Empty/absent = show all
  // (so a direct visit to the standalone page is never filtered).
  filterFabrics(fabrics) {
    // 2026-08-13: fall back to the page-level list. Only the fabrics card and the FAQ fabrics
    // link ever carried data-modal-fabrics, so the WARRANTY modal was never filtered and showed
    // PVCArmour on products that do not sell it. Any trigger without the attribute now inherits.
    if (!fabrics) fabrics = (typeof window !== 'undefined' && window.KUVR_FABRIC_COLUMNS) || '';
    if (!fabrics) return;
    const allowed = fabrics.split(',').map(s => s.trim()).filter(Boolean);
    if (!allowed.length) return;
    const root = this.modalContent;
    const cols = root.querySelectorAll('[data-fabric]');
    if (!cols.length) return; // not a fabric comparison page
    // data-fabric may list SEVERAL fabrics, comma separated — the element survives if the product
    // sells ANY of them. Needed for prose that covers a group, e.g. the warranty page's
    // "Unsupported — Flexi 30 and Ultra 50" explainer, which must vanish on a PVCArmour-only product
    // but stay for a product selling either clear fabric. A single value cannot express that.
    cols.forEach(el => {
      const mine = (el.getAttribute('data-fabric') || '').split(',').map(x => x.trim()).filter(Boolean);
      if (!mine.some(f => allowed.includes(f))) el.classList.add('is-fabric-hidden');
    });
    // Shared full-width cells span all fabric columns — shrink to the visible count
    const visible = root.querySelectorAll('.comparison-table_head[data-fabric]:not(.is-fabric-hidden)').length;
    root.querySelectorAll('.comparison-table_cell.is-shared').forEach(cell => {
      cell.setAttribute('colspan', String(visible || 1));
    });
    // Grid-based tables (the warranty pages). Three things must move together or the table
    // misaligns: the row template, the span on merged full-width cells, and the "All three
    // fabrics" badge text — all three are authored for a fixed three-fabric layout.
    const wtyRows = root.querySelectorAll('.wty-table__row');
    if (wtyRows.length) {
      // how many fabric columns survive, counted once from the head row
      const heads = root.querySelectorAll('.wty-table__cell--head[data-fabric]:not([data-fabric*=","])');
      let wtyCols = 0;
      heads.forEach(h => { if (!h.classList.contains('is-fabric-hidden')) wtyCols++; });
      if (wtyCols && wtyCols !== heads.length) {
        // EVERY row gets the same template, including rows with nothing hidden — otherwise the
        // label column is 192px on merged rows and 259px on filtered ones.
        wtyRows.forEach(row => { row.style.gridTemplateColumns = '0.85fr repeat(' + wtyCols + ', 1fr)'; });
        // merged cells are authored 'grid-column: span 3'
        root.querySelectorAll('.wty-table__cell--merged').forEach(cell => {
          cell.style.gridColumn = 'span ' + wtyCols;
        });
        // the badge literally reads "All three fabrics"
        root.querySelectorAll('.wty-table__both').forEach(badge => {
          const svg = badge.querySelector('svg');
          const label = wtyCols === 1 ? '' : (wtyCols === 2 ? 'Both fabrics' : 'All ' + wtyCols + ' fabrics');
          if (!label) { badge.style.display = 'none'; return; }
          badge.textContent = '';
          if (svg) badge.appendChild(svg);
          badge.appendChild(document.createTextNode(label));
        });
      }
    }
  }

  /* BUG-021, second cause — animated decorations absorbing the touch.
   *
   * The image-drag fix (touch-action + user-drag in kuvr-modal.css) fixed four
   * of the six modals. Free Design Service stayed bad, and it is the one running
   * 29 continuous animations; Warranty runs 2 and works. Confirmed live on
   * Duncan's iPhone: making its 28 animated decorations touch-transparent —
   * WITHOUT stopping the animations — made it noticeably better.
   *
   * So the animations are not the problem; them being hit-test targets is. Each
   * one holds a live compositing layer, and a touch landing on it does not reach
   * the scroller underneath.
   *
   * 🛑 This must NEVER stop the animations. An earlier attempt used
   * `animation: none` across modal content and broke the Free Design Service
   * page outright — those animations ARE the content, not decoration. Only
   * hit-testing is changed here; everything still animates.
   *
   * Anything interactive, or inside something interactive, is skipped, so the
   * Design first / Order first controls and the fabric video thumbnails keep
   * working. Mobile only — desktop has never shown this fault and pointer-events
   * changes would affect hover there.
   */
  makeDecorationsTouchTransparent() {
    if (!window.matchMedia('(max-width: 768px)').matches) return;
    const INTERACTIVE = 'a,button,input,select,textarea,label,summary,video,iframe,' +
                        '[data-modal-url],[onclick],[role="button"],[role="tab"],[tabindex]';
    let n = 0;
    this.modalContent.querySelectorAll('*').forEach(el => {
      if (el.dataset.kuvrTouchThrough) return;
      if (getComputedStyle(el).animationName === 'none') return;
      if (el.matches(INTERACTIVE) || el.closest(INTERACTIVE)) return;
      if (el.querySelector(INTERACTIVE)) return;   // don't orphan a control inside it
      el.style.pointerEvents = 'none';
      el.dataset.kuvrTouchThrough = '1';
      n++;
    });
    return n;
  }

  /* BUG-021 — why takeover mode exists (2026-08-10, after a full day).
   *
   * Every failure all day was in ONE mechanism: the modal's inner overflow
   * scroller. The document scroller never failed once — Duncan scrolls the site
   * itself flawlessly on the same phone. Audit of the real page showed why the
   * inner scroller keeps losing the gesture arbitration: a 25,000px document
   * behind it, 24 external + 46 inline scripts, jQuery Mobile's vmouse layer
   * (document-level capture touch handlers), a Hammer instance, the theme's
   * drawer component whose document-level touchmove handler calls
   * preventDefault() on any gesture it reads as ±25° from horizontal, ~48
   * element-level non-passive touch listeners on page images/sliders, and a
   * third-party widget pinned OVER the modal at z-index 999999.
   *
   * Takeover mode removes the contested mechanism instead of negotiating with
   * it: on mobile the page content is display:none'd (its listeners, layers and
   * widgets go with it), the modal sits in normal flow, and the DOCUMENT
   * scrolls. There is no inner scroller left to freeze.
   * Scroll position is saved on open and restored on close.
   */
  _enterTakeover() {
    this._savedScrollY = window.scrollY;
    document.documentElement.classList.add('kuvr-takeover');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  _exitTakeover() {
    document.documentElement.classList.remove('kuvr-takeover');
    window.scrollTo({ top: this._savedScrollY || 0, behavior: 'instant' });
  }

  close() {
    // Hide modal
    this.modal.setAttribute('aria-hidden', 'true');
    this.modal.classList.remove('is-active');

    if (document.documentElement.classList.contains('kuvr-takeover')) {
      this._exitTakeover();
    } else {
      KuvrScrollLock.unlock();
    }

    // Remove any video lightbox moved to body (prevents duplicate ID bug on re-open)
    const orphanedLb = document.getElementById('fabricVideoLightbox');
    if (orphanedLb && orphanedLb.parentElement === document.body) {
      const fr = document.getElementById('fabricVideoFrame');
      if (fr) fr.src = '';
      orphanedLb.remove();
    }

    // Clear content after animation
    setTimeout(() => {
      this.modalContent.innerHTML = '';
    }, 300);
  }

  isOpen() {
    return this.modal.classList.contains('is-active');
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="kuvr-modal__loading">
        <div class="kuvr-modal__spinner"></div>
        <p>Loading...</p>
      </div>
    `;
  }

  showError(url) {
    this.modalContent.innerHTML = `
      <div class="kuvr-modal__error">
        <h2>Unable to load content</h2>
        <p>Sorry, we couldn't load this content. Please try again or visit the page directly.</p>
        <a href="${url}" class="button">View Page</a>
      </div>
    `;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new KuvrModal();
  });
} else {
  new KuvrModal();
}

/**
 * Tooltip Scroll Close
 * Close tooltips when user scrolls (mobile UX fix)
 */
window.addEventListener('scroll', function() {
  document.querySelectorAll('.atc-tooltip-trigger.is-active').forEach(function(t) {
    t.classList.remove('is-active');
  });
}, { passive: true });


/* KUVR AJAX SECTION LOADER
 * Finds .kuvr-ajax-loader[data-page] divs and fetches content via /pages/{handle}?view=kuvr_ajax
 * Loaded client-side only — not in server HTML, so no duplicate content across products
 */
(function() {
  function loadAjaxSections() {
    document.querySelectorAll('.kuvr-ajax-loader[data-page]:not([data-loaded])').forEach(function(el) {
      var handle = el.getAttribute('data-page');
      el.setAttribute('data-loaded', '1');
      fetch('/pages/' + handle + '?view=kuvr_ajax')
        .then(function(r) {
          // A deleted/missing page does NOT reject the fetch — Shopify returns a
          // 404 with the FULL storefront document (including its own
          // <preload-screen> overlay). Bail out on any non-OK status so we never
          // inject that document and trap the page behind a stuck loading screen.
          if (!r.ok) throw new Error('kuvr-ajax-loader: HTTP ' + r.status + ' for ' + handle);
          return r.text();
        })
        .then(function(html) {
          // Defensive guard for soft-404s (200 status but error-page body):
          // never inject a full HTML document or anything carrying a preload screen.
          if (/<!doctype|<html[\s>]|preload-screen/i.test(html)) {
            console.warn('kuvr-ajax-loader: ignoring full-document/error response for', handle);
            return;
          }
          el.innerHTML = html;
        })
        .catch(function(e) { console.warn('kuvr-ajax-loader: failed', handle, e); });
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadAjaxSections);
  } else {
    loadAjaxSections();
  }
})();

/**
 * ATC Tooltip Handlers — desktop hover + mobile tap toggle.
 * CSS handles all positioning (position:absolute, bottom:calc(100%+8px), right:0).
 * JS only toggles is-active class and visibility.
 */
(function() {
  var isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

  function closeAll() {
    document.querySelectorAll('.atc-tooltip-trigger.is-active').forEach(function(t) {
      t.classList.remove('is-active');
    });
  }

  function initAtcTooltips() {
    document.querySelectorAll('.atc-tooltip-trigger').forEach(function(btn) {
      var tooltip = btn.querySelector('.atc-tooltip');
      if (!tooltip) return;

      if (isTouchDevice) {
        btn.addEventListener('click', function(e) {
          e.stopPropagation();
          var isOpen = btn.classList.contains('is-active');
          closeAll();
          if (!isOpen) btn.classList.add('is-active');
        });
      } else {
        btn.addEventListener('mouseenter', function() {
          closeAll();
          btn.classList.add('is-active');
        });
        btn.addEventListener('mouseleave', closeAll);
      }
    });
    document.addEventListener('click', closeAll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAtcTooltips);
  } else {
    initAtcTooltips();
  }
})();


/* DPO → measuring-guide shape sync
 * When a DPO shape option radio is selected, auto-activates the matching
 * measuring guide thumbnail. Uses exact match (trimmed, case-insensitive)
 * to avoid the substring bug where "Round Daybed" would match
 * "Round Daybed with Canopy" via dpoLabel.toLowerCase().includes(shapeName.toLowerCase()).
 */
(function() {
  function syncMG(dpoLabel) {
    var norm = (dpoLabel || '').trim().toLowerCase();
    if (!norm) return;
    document.querySelectorAll('.kuvr-mg__thumb').forEach(function(btn) {
      var span = btn.querySelector('.kuvr-mg__thumb-name');
      if (span && span.textContent.trim().toLowerCase() === norm) btn.click();
    });
  }

  document.addEventListener('change', function(e) {
    var el = e.target;
    if (!el || el.type !== 'radio') return;
    if (!el.name || !/^options\[\d+\]/.test(el.name)) return;
    var wrap = el.closest('label') || el.parentElement;
    var label = ((wrap && wrap.textContent) || '').replace(/\s+/g, ' ').trim();
    if (!label) label = el.value || '';
    syncMG(label);
  }, true);
})();
